import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Layers, Save, Loader, Search } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { festivalsAPI, tournamentSeriesAPI, handleApiError } from '../utils/api';

/**
 * Page dédiée à la gestion des séries d'un festival (structures
 * multi-jours : Day 1A / Day 1B / Final Day) : créer, renommer, supprimer
 * une série, et choisir en une fois quels tournois du festival en font
 * partie (cases à cocher + "Enregistrer les tournois").
 */
const ManageSeriesPage = () => {
  const navigate = useNavigate();
  const { festivalId } = useParams();
  const { userRole } = useAuth();

  const [festival, setFestival] = useState(null);
  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [busySeriesId, setBusySeriesId] = useState(null);
  // Filtre sur le titre des tournois, commun à toutes les séries affichées
  const [filter, setFilter] = useState('');
  // Séries actuellement en mode édition (cases à cocher + tous les tournois) ;
  // les autres sont en mode lecture (uniquement les tournois rattachés).
  const [editingIds, setEditingIds] = useState(new Set());

  // Sélection en cours par série : { [seriesId]: Set<tournamentId> }, initialisée
  // depuis les tournois actuellement rattachés, modifiée par les cases à cocher
  // tant qu'on n'a pas cliqué sur "Enregistrer les tournois".
  const [selection, setSelection] = useState({});

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [festivalResponse, seriesResponse] = await Promise.all([
        festivalsAPI.getById(festivalId),
        tournamentSeriesAPI.getAll(festivalId),
      ]);
      const loadedFestival = festivalResponse.data;
      const loadedSeries = seriesResponse.data || [];
      setFestival(loadedFestival);
      setSeries(loadedSeries);

      const initial = {};
      loadedSeries.forEach((s) => {
        initial[s.id] = new Set(
          (loadedFestival.subEvents || [])
            .filter((t) => String(t.detailedInfo?.seriesId) === String(s.id))
            .map((t) => t.id)
        );
      });
      setSelection(initial);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [festivalId]);

  useEffect(() => {
    load();
  }, [load]);

  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
          <button onClick={() => navigate('/')} className="bg-blue-600 text-white px-6 py-2 rounded-lg">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (error || !festival) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur</h2>
          {error && <p className="text-gray-600 mb-6">{error}</p>}
          <button
            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments`)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  const tournaments = [...(festival.subEvents || [])].sort(
    (a, b) => new Date(a.date + ' ' + a.time) - new Date(b.date + ' ' + b.time)
  );
  // Comparaison insensible à la casse et aux accents
  const normalize = (text) =>
    (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filterTerm = normalize(filter.trim());
  const visibleTournaments = filterTerm
    ? tournaments.filter((t) => normalize(t.title).includes(filterTerm))
    : tournaments;
  const seriesNameById = Object.fromEntries(series.map((s) => [String(s.id), s.name]));

  // Série actuellement enregistrée d'un tournoi (null si indépendant)
  const currentSeriesOf = (t) => t.detailedInfo?.seriesId ?? null;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await tournamentSeriesAPI.create(festivalId, newName.trim(), null);
      setNewName('');
      await load();
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de créer la série : ${errTitle} - ${details}`);
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (s) => {
    const name = window.prompt('Nouveau nom de la série :', s.name);
    if (name === null || !name.trim() || name.trim() === s.name) return;
    setBusySeriesId(s.id);
    try {
      await tournamentSeriesAPI.update(festivalId, s.id, {
        name: name.trim(),
        totalEntries: s.totalEntries ?? null,
      });
      await load();
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de renommer : ${errTitle} - ${details}`);
    } finally {
      setBusySeriesId(null);
    }
  };

  const handleDelete = async (s) => {
    const count = tournaments.filter((t) => String(currentSeriesOf(t)) === String(s.id)).length;
    if (!window.confirm(
      `Supprimer la série "${s.name}" ?\n\n` +
      `Les ${count} tournoi(s) rattaché(s) ne sont PAS supprimés : ils redeviennent des tournois indépendants.\n` +
      `Les résultats de série déjà saisis par les joueurs (position, cashout, entrées) seront en revanche perdus.\n\n` +
      `Cette action est irréversible.`
    )) {
      return;
    }
    setBusySeriesId(s.id);
    try {
      await tournamentSeriesAPI.delete(festivalId, s.id);
      await load();
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer la série : ${errTitle} - ${details}`);
    } finally {
      setBusySeriesId(null);
    }
  };

  const toggleTournament = (seriesId, tournamentId) => {
    setSelection((prev) => {
      const next = new Set(prev[seriesId] || []);
      if (next.has(tournamentId)) next.delete(tournamentId);
      else next.add(tournamentId);
      return { ...prev, [seriesId]: next };
    });
  };

  const isDirty = (s) => {
    const selected = selection[s.id] || new Set();
    const saved = tournaments.filter((t) => String(currentSeriesOf(t)) === String(s.id)).map((t) => t.id);
    return selected.size !== saved.length || saved.some((id) => !selected.has(id));
  };

  const savedIdsOf = (s) =>
    tournaments.filter((t) => String(currentSeriesOf(t)) === String(s.id)).map((t) => t.id);

  const setEditing = (seriesId, on) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(seriesId);
      else next.delete(seriesId);
      return next;
    });
  };

  const handleCancelEdit = (s) => {
    // Abandonne les modifications non enregistrées de cette série
    setSelection((prev) => ({ ...prev, [s.id]: new Set(savedIdsOf(s)) }));
    setEditing(s.id, false);
  };

  const handleSaveTournaments = async (s) => {
    setBusySeriesId(s.id);
    try {
      await tournamentSeriesAPI.setTournaments(festivalId, s.id, Array.from(selection[s.id] || []));
      await load();
      setEditing(s.id, false);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'enregistrer les tournois : ${errTitle} - ${details}`);
    } finally {
      setBusySeriesId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(`/manage-festivals/${festivalId}/tournaments`)}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour aux tournois
          </button>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h1 className="text-3xl font-bold flex items-center text-gray-800 mb-1">
              <Layers className="mr-3 text-purple-600" size={32} />
              Séries
            </h1>
            <p className="text-gray-600">Festival : {festival.title}</p>
            <p className="text-sm text-gray-500 mt-2">
              Une série regroupe les days d'une même structure multi-jours (ex: Day 1A, Day 1B, Final Day).
              Clique sur « Modifier les tournois » pour choisir ceux qui en font partie.
            </p>
          </div>

          <form onSubmit={handleCreate} className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[16rem]">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nouvelle série</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: PokerStars Open Main Event"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={creating || !newName.trim()}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-lg font-semibold flex items-center disabled:opacity-50"
            >
              {creating ? <Loader size={18} className="mr-2 animate-spin" /> : <Plus size={18} className="mr-2" />}
              Créer la série
            </button>
          </form>

          {editingIds.size > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center gap-3">
              <Search size={18} className="text-gray-400 shrink-0" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrer les tournois par nom..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {filter && (
                <button
                  type="button"
                  onClick={() => setFilter('')}
                  className="text-sm text-blue-600 hover:underline whitespace-nowrap"
                >
                  Effacer
                </button>
              )}
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {visibleTournaments.length}/{tournaments.length}
              </span>
            </div>
          )}

          {series.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Aucune série pour ce festival.
            </div>
          ) : (
            <div className="space-y-4">
              {series.map((s) => {
                const busy = busySeriesId === s.id;
                const selected = selection[s.id] || new Set();
                const editing = editingIds.has(s.id);
                const savedIds = savedIdsOf(s);
                const shownCount = editing ? selected.size : savedIds.length;
                return (
                  <div key={s.id} className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-purple-100">
                    <div className="p-4 bg-purple-50 flex items-center justify-between flex-wrap gap-3">
                      <h2 className="font-bold text-gray-800 flex items-center gap-2">
                        <Layers size={18} className="text-purple-600" />
                        {s.name}
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                          {shownCount} tournoi{shownCount > 1 ? 's' : ''}
                        </span>
                      </h2>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleRename(s)}
                          disabled={busy}
                          className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                          title="Renommer"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(s)}
                          disabled={busy}
                          className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                          title="Supprimer la série"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="p-4">
                      {editing ? (
                        <>
                          {tournaments.length === 0 ? (
                            <p className="text-sm text-gray-500">Ce festival n'a pas encore de tournoi.</p>
                          ) : visibleTournaments.length === 0 ? (
                            <p className="text-sm text-gray-500">Aucun tournoi ne correspond au filtre.</p>
                          ) : (
                            <ul className="divide-y divide-gray-100">
                              {visibleTournaments.map((t) => {
                                const other = currentSeriesOf(t);
                                const inOther = other && String(other) !== String(s.id);
                                return (
                                  <li key={t.id}>
                                    <label className="flex items-center gap-3 py-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selected.has(t.id)}
                                        onChange={() => toggleTournament(s.id, t.id)}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                      />
                                      <span className="flex-1 text-gray-800">
                                        {t.title}
                                        <span className="ml-2 text-xs text-gray-500">
                                          {new Date(t.date).toLocaleDateString('fr-FR')} {t.time}
                                        </span>
                                      </span>
                                      {inOther && (
                                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                                          Actuellement dans « {seriesNameById[String(other)] || 'une autre série'} »
                                        </span>
                                      )}
                                    </label>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          <div className="mt-3 flex flex-wrap items-center gap-3">
                            <button
                              onClick={() => handleSaveTournaments(s)}
                              disabled={busy || !isDirty(s)}
                              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center gap-2"
                            >
                              {busy ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                              Enregistrer les tournois
                            </button>
                            <button
                              onClick={() => handleCancelEdit(s)}
                              disabled={busy}
                              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                            >
                              Annuler
                            </button>
                            <span className="text-xs text-gray-500">
                              Cocher un tournoi d'une autre série l'y retire pour l'ajouter ici.
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          {savedIds.length === 0 ? (
                            <p className="text-sm text-gray-500">Aucun tournoi rattaché à cette série.</p>
                          ) : (
                            <ul className="divide-y divide-gray-100">
                              {tournaments
                                .filter((t) => savedIds.includes(t.id))
                                .map((t) => (
                                  <li key={t.id} className="py-2 text-gray-800">
                                    {t.title}
                                    <span className="ml-2 text-xs text-gray-500">
                                      {new Date(t.date).toLocaleDateString('fr-FR')} {t.time}
                                    </span>
                                  </li>
                                ))}
                            </ul>
                          )}
                          <div className="mt-3">
                            <button
                              onClick={() => setEditing(s.id, true)}
                              disabled={busy}
                              className="bg-purple-100 text-purple-800 px-4 py-2 rounded-lg hover:bg-purple-200 transition disabled:opacity-50 flex items-center gap-2"
                            >
                              <Edit size={16} />
                              Modifier les tournois
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageSeriesPage;
